import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import html2canvas from "html2canvas";
import { formatDrawDate } from "../../../utils/reportServerSide";

const money = (v) => Number(v || 0).toFixed(2);

const TAB_TITLES = {
  markets: "Market Summary",
  matches: "Match Summary",
  users: "User Summary",
};

const PAGE = { w: 842, h: 595, margin: 40 };

const buildColumns = (tab) => {
  if (tab === "matches") {
    return [
      { header: "Market", dataKey: "market_name" },
      { header: "Match", dataKey: "match_name" },
      { header: "Draw Date", dataKey: "draw_date" },
      { header: "Bets", dataKey: "bet_count" },
      { header: "Users", dataKey: "unique_users" },
      { header: "Stake", dataKey: "total_stake" },
      { header: "Payout", dataKey: "total_payout" },
      { header: "P/L", dataKey: "profit_loss" },
    ];
  }

  if (tab === "users") {
    return [
      { header: "User ID", dataKey: "user_id" },
      { header: "Username", dataKey: "username" },
      { header: "Market", dataKey: "market_name" },
      { header: "Match", dataKey: "match_name" },
      { header: "Draw Date", dataKey: "draw_date" },
      { header: "Bets", dataKey: "bet_count" },
      { header: "Stake", dataKey: "total_stake" },
      { header: "Payout", dataKey: "total_payout" },
      { header: "P/L", dataKey: "profit_loss" },
    ];
  }

  return [
    { header: "Market", dataKey: "market_name" },
    { header: "Matches", dataKey: "match_count" },
    { header: "Bets", dataKey: "bet_count" },
    { header: "Users", dataKey: "unique_users" },
    { header: "Stake", dataKey: "total_stake" },
    { header: "Payout", dataKey: "total_payout" },
    { header: "P/L", dataKey: "profit_loss" },
  ];
};

const formatCell = (key, row) => {
  const value = row[key];
  if (key === "draw_date") {
    return value ? formatDrawDate(value) : "—";
  }
  if (["total_stake", "total_payout", "profit_loss"].includes(key)) {
    return money(value);
  }
  if (value == null || value === "") return "—";
  return String(value);
};

export const captureReportChartImages = async (rootEl) => {
  if (!rootEl) return [];

  const nodes = rootEl.querySelectorAll("[data-report-chart='true']");
  const images = [];

  for (const node of nodes) {
    try {
      const canvas = await html2canvas(node, {
        scale: 2,
        backgroundColor: "#ffffff",
        logging: false,
        useCORS: true,
      });
      images.push({
        title: node.getAttribute("data-chart-title") || "Chart",
        dataUrl: canvas.toDataURL("image/png"),
      });
    } catch (error) {
      console.error("Failed to capture chart for PDF:", error);
    }
  }

  return images;
};

const addSectionTitle = (doc, text, y) => {
  doc.setFontSize(12);
  doc.setTextColor(30);
  doc.text(text, PAGE.margin, y);
  return y + 16;
};

const ensureSpace = (doc, y, needed) => {
  if (y + needed > PAGE.h - PAGE.margin) {
    doc.addPage();
    return PAGE.margin;
  }
  return y;
};

const addChartImages = (doc, chartImages, startY) => {
  if (!chartImages?.length) return startY;

  let y = addSectionTitle(doc, "Charts", startY);
  const gap = 12;
  const colW = (PAGE.w - PAGE.margin * 2 - gap) / 2;
  const rowH = 180;

  for (let i = 0; i < chartImages.length; i += 2) {
    y = ensureSpace(doc, y, rowH + 24);

    const left = chartImages[i];
    const right = chartImages[i + 1];

    const drawOne = (img, x) => {
      if (!img?.dataUrl) return;
      doc.setFontSize(9);
      doc.setTextColor(60);
      doc.text(img.title, x, y);
      const imgW = colW;
      const imgH = rowH - 14;
      doc.addImage(img.dataUrl, "PNG", x, y + 6, imgW, imgH);
    };

    drawOne(left, PAGE.margin);
    if (right) drawOne(right, PAGE.margin + colW + gap);

    y += rowH + 18;
  }

  return y;
};

const addChartDataTables = (doc, tab, charts, sessionBreakdown, startY) => {
  let y = startY;

  const entityRows =
    tab === "users" ? charts?.topUsers || [] : charts?.byEntity || [];

  if (entityRows.length) {
    y = ensureSpace(doc, y, 80);
    y = addSectionTitle(
      doc,
      tab === "users" ? "Chart Data — Top Users" : "Chart Data — By Entity",
      y
    );

    autoTable(doc, {
      startY: y,
      head: [["Name", "Bets", "Stake", "Payout", "P/L"]],
      body: entityRows.map((r) => [
        r.name || r.id || "—",
        String(r.bet_count ?? 0),
        money(r.total_stake),
        money(r.total_payout),
        money(r.profit_loss),
      ]),
      styles: { fontSize: 8, cellPadding: 3 },
      headStyles: { fillColor: [79, 70, 229] },
      margin: { left: PAGE.margin, right: PAGE.margin },
    });
    y = (doc.lastAutoTable?.finalY || y) + 18;
  }

  if (tab === "users" && charts?.userOutcomes) {
    const o = charts.userOutcomes;
    y = ensureSpace(doc, y, 70);
    y = addSectionTitle(doc, "Chart Data — User Outcomes", y);
    autoTable(doc, {
      startY: y,
      head: [["Outcome", "Users"]],
      body: [
        ["Profitable", String(o.profitable_users ?? 0)],
        ["Loss-making", String(o.loss_making_users ?? 0)],
        ["Break-even", String(o.break_even_users ?? 0)],
        ["Total", String(o.total_users ?? 0)],
      ],
      styles: { fontSize: 8, cellPadding: 3 },
      headStyles: { fillColor: [79, 70, 229] },
      margin: { left: PAGE.margin, right: PAGE.margin },
    });
    y = (doc.lastAutoTable?.finalY || y) + 18;
  }

  if (sessionBreakdown) {
    const open = sessionBreakdown.open || {};
    const close = sessionBreakdown.close || {};
    y = ensureSpace(doc, y, 70);
    y = addSectionTitle(doc, "Chart Data — Open vs Close Session", y);
    autoTable(doc, {
      startY: y,
      head: [["Session", "Bets", "Users", "Stake", "Payout", "P/L"]],
      body: [
        [
          "Open",
          String(open.bet_count ?? 0),
          String(open.unique_users ?? 0),
          money(open.total_stake),
          money(open.total_payout),
          money(open.profit_loss),
        ],
        [
          "Close",
          String(close.bet_count ?? 0),
          String(close.unique_users ?? 0),
          money(close.total_stake),
          money(close.total_payout),
          money(close.profit_loss),
        ],
      ],
      styles: { fontSize: 8, cellPadding: 3 },
      headStyles: { fillColor: [79, 70, 229] },
      margin: { left: PAGE.margin, right: PAGE.margin },
    });
    y = (doc.lastAutoTable?.finalY || y) + 18;
  }

  if (tab !== "users" && charts?.topUsersByPayout?.length) {
    y = ensureSpace(doc, y, 80);
    y = addSectionTitle(doc, "Top 5 Users", y);
    autoTable(doc, {
      startY: y,
      head: [["#", "User", "User ID", "Total Payout", "Total Matches", "Win %"]],
      body: charts.topUsersByPayout.map((u, idx) => [
        String(idx + 1),
        u.username || "—",
        String(u.user_id || "—"),
        money(u.total_payout),
        String(u.total_matches ?? 0),
        `${Number(u.win_percentage || 0).toFixed(2)}%`,
      ]),
      styles: { fontSize: 8, cellPadding: 3 },
      headStyles: { fillColor: [79, 70, 229] },
      margin: { left: PAGE.margin, right: PAGE.margin },
    });
    y = (doc.lastAutoTable?.finalY || y) + 18;
  }

  return y;
};

export const exportReportPdf = async ({
  tab,
  filters,
  kpis,
  rows,
  charts,
  sessionBreakdown,
  chartImages = [],
}) => {
  const doc = new jsPDF({ orientation: "landscape", unit: "pt", format: "a4" });
  const title = TAB_TITLES[tab] || "Report";
  const dateLabel =
    filters?.start_date === filters?.end_date
      ? filters?.start_date || ""
      : `${filters?.start_date || ""} to ${filters?.end_date || ""}`;

  doc.setFontSize(16);
  doc.text(`Matka Reports — ${title}`, 40, 40);

  doc.setFontSize(10);
  doc.setTextColor(80);
  doc.text(`Date: ${dateLabel}`, 40, 58);
  doc.text(
    `Filters: Market=${filters?.market_id || "all"}, Match=${filters?.match_id || "all"}, Operator=${filters?.operator_id || "all"}, Session=${filters?.status ?? "all"}`,
    40,
    72
  );

  const pl = Number(kpis?.profit_loss) || 0;
  const kpiLines = [
    `Stake: ${money(kpis?.total_stake)}`,
    `Payout: ${money(kpis?.total_payout)}`,
    `P/L: ${money(pl)}`,
    `Bets: ${kpis?.bet_count ?? 0}`,
    `Users: ${kpis?.unique_users ?? 0}`,
  ];

  if (tab === "users") {
    kpiLines.push(
      `Profitable: ${kpis?.profitable_users ?? 0}`,
      `Loss-making: ${kpis?.loss_making_users ?? 0}`,
      `Avg Winning/User: ${money(kpis?.avg_winning_per_user)}`,
      `Avg Win Amt: ${money(kpis?.avg_win_amount)}`,
      `Avg Bets/User: ${money(kpis?.avg_bets_per_user)}`,
      `Avg Stake/User: ${money(kpis?.avg_stake_per_user)}`,
      `Biggest Won: ${money(kpis?.biggest_won_amount)}`
    );
  } else if (tab === "markets") {
    kpiLines.push(
      `Markets: ${kpis?.market_count ?? 0}`,
      `Matches: ${kpis?.match_count ?? 0}`
    );
  } else {
    kpiLines.push(`Matches: ${kpis?.match_count ?? 0}`);
  }

  doc.setTextColor(30);
  doc.text(kpiLines.join("  |  "), 40, 90);

  let y = 108;
  y = addChartImages(doc, chartImages, y);
  y = addChartDataTables(doc, tab, charts, sessionBreakdown, y + 4);

  y = ensureSpace(doc, y, 60);
  y = addSectionTitle(doc, "Detailed Table", y);

  const columns = buildColumns(tab);
  const body = (rows || []).map((row) =>
    columns.map((col) => formatCell(col.dataKey, row))
  );

  autoTable(doc, {
    startY: y,
    head: [columns.map((c) => c.header)],
    body,
    styles: { fontSize: 8, cellPadding: 3 },
    headStyles: { fillColor: [79, 70, 229] },
    margin: { left: PAGE.margin, right: PAGE.margin },
  });

  const fileSafeDate =
    filters?.start_date === filters?.end_date
      ? filters?.start_date || "export"
      : `${filters?.start_date || "start"}_${filters?.end_date || "end"}`;
  const fileName = `matka-${tab}-report-${fileSafeDate}.pdf`;
  doc.save(fileName);
};
